package com.havn.app.features.tasks.presentation

import com.havn.app.features.auth.domain.AuthRepository
import com.havn.app.features.auth.domain.AuthState
import com.havn.app.features.tasks.domain.HavnTask
import com.havn.app.features.tasks.domain.HavnTaskDraft
import com.havn.app.features.tasks.domain.HavnTaskRepository
import com.havn.app.features.tasks.domain.HavnTaskStatus
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

class TasksViewModel(
    private val taskRepository: HavnTaskRepository,
    private val authRepository: AuthRepository,
    private val scope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
) {
    private val _uiState = MutableStateFlow(TasksUiState())
    val uiState: StateFlow<TasksUiState> = _uiState.asStateFlow()
    private var agentId: String? = null
    private var agencyId: String? = null
    private var hasLoadedAgencyContext = false

    init {
        scope.launch {
            authRepository.authState.collect { state ->
                val nextAgentId = (state as? AuthState.LoggedIn)?.user?.id
                if (nextAgentId == null) {
                    agentId = null
                    hasLoadedAgencyContext = false
                    _uiState.value = TasksUiState()
                } else if (nextAgentId != agentId) {
                    agentId = nextAgentId
                    if (hasLoadedAgencyContext) {
                        loadTasks(nextAgentId)
                    }
                }
            }
        }
    }

    fun setAgencyContext(nextAgencyId: String?) {
        if (agencyId == nextAgencyId) return
        agencyId = nextAgencyId
        hasLoadedAgencyContext = true
        refresh()
    }

    fun refresh() {
        val currentAgentId = agentId ?: return
        scope.launch { loadTasks(currentAgentId) }
    }

    fun selectTask(taskId: String?) {
        _uiState.update { it.copy(selectedTaskId = taskId) }
    }

    fun acceptSuggestedTask(draft: HavnTaskDraft) {
        val currentAgentId = agentId ?: return
        scope.launch {
            _uiState.update { it.copy(isSaving = true, errorMessage = null) }
            taskRepository.createTask(currentAgentId, draft)
                .onSuccess { task ->
                    _uiState.update { state ->
                        state.copy(
                            isSaving = false,
                            tasks = listOf(task) + state.tasks.filterNot { it.id == task.id },
                            dismissedSuggestionIds = state.dismissedSuggestionIds + draft.suggestionKey()
                        )
                    }
                }
                .onFailure { error ->
                    _uiState.update { it.copy(isSaving = false, errorMessage = error.message ?: "Could not save task.") }
                }
        }
    }

    fun dismissSuggestion(suggestionId: String) {
        _uiState.update { it.copy(dismissedSuggestionIds = it.dismissedSuggestionIds + suggestionId) }
    }

    fun markDone(taskId: String) {
        updateStatus(taskId, HavnTaskStatus.Done)
    }

    fun reopen(taskId: String) {
        updateStatus(taskId, HavnTaskStatus.Open)
    }

    fun dispose() {
        scope.cancel()
    }

    private fun updateStatus(taskId: String, status: HavnTaskStatus) {
        val currentAgentId = agentId ?: return
        scope.launch {
            _uiState.update { it.copy(isSaving = true, errorMessage = null) }
            taskRepository.updateStatus(
                agentId = currentAgentId,
                taskId = taskId,
                status = status,
                completedAt = if (status == HavnTaskStatus.Done) nowIsoLike() else null
            ).onSuccess { updated ->
                _uiState.update { state ->
                    state.copy(
                        isSaving = false,
                        tasks = state.tasks.map { if (it.id == updated.id) updated else it }
                    )
                }
            }.onFailure { error ->
                _uiState.update { it.copy(isSaving = false, errorMessage = error.message ?: "Could not update task.") }
            }
        }
    }

    private suspend fun loadTasks(currentAgentId: String) {
        _uiState.update { it.copy(isLoading = true, errorMessage = null) }
        taskRepository.tasksForAgent(currentAgentId, agencyId)
            .onSuccess { tasks ->
                _uiState.update { it.copy(isLoading = false, tasks = tasks) }
            }
            .onFailure { error ->
                _uiState.update { it.copy(isLoading = false, errorMessage = error.message ?: "Could not load tasks.") }
            }
    }
}

data class TasksUiState(
    val tasks: List<HavnTask> = emptyList(),
    val selectedTaskId: String? = null,
    val dismissedSuggestionIds: Set<String> = emptySet(),
    val isLoading: Boolean = false,
    val isSaving: Boolean = false,
    val errorMessage: String? = null
)

private fun HavnTaskDraft.suggestionKey(): String =
    listOf(type.databaseValue, propertyId, openHouseId, leadId, followUpId, title).joinToString(":")

@OptIn(ExperimentalTime::class)
private fun nowIsoLike(): String = Clock.System.now().toString()
